import { $createParagraphNode, $getSelection, $isRangeSelection, type LexicalEditor } from 'lexical';
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import { $createCodeNode } from '@lexical/code';
import { $createHeadingNode, $createQuoteNode, type HeadingTagType } from '@lexical/rich-text';
import { type listTypeToListName, type blockTypeToBlockName } from '../constants/constants';
import { INSERT_EQUATION_COMMAND } from '../../EquationsPlugin';
type BLOCK_PROPS = {
  editor: LexicalEditor;
  type: keyof typeof listTypeToListName | keyof typeof blockTypeToBlockName;
};
const formatHeading = ({
  editor,
  type,
  headingSize
}: {
  editor: LexicalEditor;
  type: keyof typeof listTypeToListName | keyof typeof blockTypeToBlockName;
  headingSize: HeadingTagType;
}) => {
  if (type !== headingSize) {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  }
};
export const BLOCKS = {
  bullet: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    if (type !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  },
  check: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    if (type !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  },
  number: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    if (type !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  },
  paragraph: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createParagraphNode());
    });
  },
  h1: ({
    editor,
    type
  }: BLOCK_PROPS) => formatHeading({
    editor,
    type,
    headingSize: 'h1'
  }),
  h2: ({
    editor,
    type
  }: BLOCK_PROPS) => formatHeading({
    editor,
    type,
    headingSize: 'h2'
  }),
  h3: ({
    editor,
    type
  }: BLOCK_PROPS) => formatHeading({
    editor,
    type,
    headingSize: 'h3'
  }),
  h4: ({
    editor,
    type
  }: BLOCK_PROPS) => formatHeading({
    editor,
    type,
    headingSize: 'h4'
  }),
  h5: ({
    editor,
    type
  }: BLOCK_PROPS) => formatHeading({
    editor,
    type,
    headingSize: 'h5'
  }),
  h6: ({
    editor,
    type
  }: BLOCK_PROPS) => formatHeading({
    editor,
    type,
    headingSize: 'h6'
  }),
  quote: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    if (type !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createQuoteNode());
      });
    }
  },
  code: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    if (type !== 'code') {
      editor.update(() => {
        let selection = $getSelection();
        if (selection === null) {
          return;
        }
        if (selection.isCollapsed()) {
          $setBlocksType(selection, () => $createCodeNode());
        } else {
          const textContent = selection.getTextContent();
          const codeNode = $createCodeNode();
          selection.insertNodes([codeNode]);
          selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertRawText(textContent);
          }
        }
      });
    }
  },
  equation: ({
    editor,
    type
  }: BLOCK_PROPS) => {
    if (type !== 'equation') {
      editor.update(() => {
        editor.dispatchCommand(INSERT_EQUATION_COMMAND, {
          equation: '',
          inline: true
        });
      });
    }
  },
  default: () => {}
};